'use client';

import type { MysteryRoomDoorRuntimeState, MysteryRoomLocalRuntime } from '../core/localRuntime';
import type { MysteryRoomRect, MysteryRoomTriggerAction, MysteryRoomVec2 } from '../world/mapTypes';
import { rectCenter, rectContainsPoint } from '../world/mapTypes';

export type MysteryRoomLocalActionSource = {
  id: string;
  label: string;
  zoneId: string;
  rect?: MysteryRoomRect;
  position?: MysteryRoomVec2;
};

export function updateLocalTriggers(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  let next = runtime;

  for (const trigger of runtime.triggers) {
    if (!rectContainsPoint(trigger.rect, runtime.hero.position)) {
      continue;
    }
    if (!trigger.repeatable && trigger.lastActivatedAtMs !== null) {
      continue;
    }
    if (
      trigger.lastActivatedAtMs !== null &&
      runtime.elapsedMs - trigger.lastActivatedAtMs < Math.max(0, trigger.cooldownMs)
    ) {
      continue;
    }
    next = applyLocalTriggerActions(
      next,
      {
        id: trigger.id,
        label: trigger.label,
        zoneId: trigger.zoneId,
        rect: trigger.rect,
      },
      trigger.actions,
      { markTriggerId: trigger.id },
    );
  }

  return next;
}

export function applyLocalTriggerActions(
  runtime: MysteryRoomLocalRuntime,
  source: MysteryRoomLocalActionSource,
  actions: MysteryRoomTriggerAction[],
  options: { markTriggerId?: string } = {},
): MysteryRoomLocalRuntime {
  if (actions.length === 0) {
    return runtime;
  }

  let lights = runtime.lights;
  let doors = runtime.doors;
  let hazards = runtime.hazards;
  let soundSources = runtime.soundSources;
  let worldAlarm = runtime.worldAlarm;
  const events: string[] = [];

  const sourcePosition = source.position ?? (source.rect ? rectCenter(source.rect) : runtime.hero.position);

  for (const action of actions) {
    switch (action.kind) {
      case 'toggle_light':
        lights = lights.map((light) =>
          action.lightIds.includes(light.id) ? { ...light, enabled: !light.enabled } : light,
        );
        events.push(`${source.label}: переключен свет`);
        break;
      case 'set_light':
        lights = lights.map((light) =>
          action.lightIds.includes(light.id) ? { ...light, enabled: action.enabled } : light,
        );
        events.push(`${source.label}: свет ${action.enabled ? 'включен' : 'выключен'}`);
        break;
      case 'set_light_flicker':
        lights = lights.map((light) =>
          action.lightIds.includes(light.id)
            ? {
                ...light,
                flickerMode: action.enabled ? 'event_flicker' : 'steady',
                eventFlickerUntilMs: action.enabled
                  ? runtime.elapsedMs + Math.max(250, action.durationMs ?? 3000)
                  : 0,
              }
            : light,
        );
        events.push(`${source.label}: мерцание света ${action.enabled ? 'включено' : 'выключено'}`);
        break;
      case 'open_door':
        doors = setDoorState(doors, action.doorIds, 'open');
        events.push(`${source.label}: дверь открыта`);
        break;
      case 'close_door':
        doors = setDoorState(doors, action.doorIds, 'closed');
        events.push(`${source.label}: дверь закрыта`);
        break;
      case 'spawn_smoke':
        hazards = [
          ...hazards,
          {
            id: action.hazardId ?? `${source.id}_smoke_${Math.round(runtime.elapsedMs)}`,
            kind: 'smoke',
            zoneId: source.zoneId,
            position: action.position ?? sourcePosition,
            radius: action.radius ?? (source.rect ? Math.max(source.rect.width, source.rect.height) : 140),
            intensity: 0.78,
          },
        ];
        events.push(`${source.label}: дым`);
        break;
      case 'explosion':
        hazards = [
          ...hazards,
          {
            id: `${source.id}_sparks_${Math.round(runtime.elapsedMs)}`,
            kind: 'sparks',
            zoneId: source.zoneId,
            position: action.position ?? sourcePosition,
            radius: action.radius ?? (source.rect ? Math.max(source.rect.width, source.rect.height) : 160),
            intensity: 1,
          },
        ];
        events.push(`${source.label}: вспышка`);
        break;
      case 'play_sound':
        soundSources = soundSources.map((source) =>
          action.soundSourceIds.includes(source.id) ? { ...source, active: true, lastPlayedAtMs: runtime.elapsedMs } : source,
        );
        events.push(`${source.label}: звук`);
        break;
      case 'set_alarm':
        worldAlarm = {
          state: action.enabled ? 'active' : 'clear',
          sourceIncidentId: action.enabled ? source.id : null,
          activeUntilMs: action.enabled ? runtime.elapsedMs + 15000 : 0,
        };
        events.push(`${source.label}: ${action.enabled ? 'тревога' : 'тревога снята'}`);
        break;
      case 'enable_equipment':
        events.push(`${source.label}: оборудование ${action.enabled ? 'включено' : 'выключено'}`);
        break;
      default:
        break;
    }
  }

  return {
    ...runtime,
    lights,
    doors,
    hazards,
    soundSources,
    worldAlarm,
    triggers: runtime.triggers.map((entry) =>
      entry.id === options.markTriggerId ? { ...entry, lastActivatedAtMs: runtime.elapsedMs } : entry,
    ),
    events: [...events, ...runtime.events].slice(0, 10),
  };
}

function setDoorState(
  doors: MysteryRoomDoorRuntimeState[],
  doorIds: string[],
  state: 'open' | 'closed',
): MysteryRoomDoorRuntimeState[] {
  return doors.map((door) =>
    doorIds.includes(door.id)
      ? {
          ...door,
          state,
          openProgress: state === 'open' ? 1 : 0,
        }
      : door,
  );
}
